import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { DiscordIcon } from '@/components/icons';
import { WordsPullUp } from '@/components/animations';

// Published Google Sheet (CSV). Columns in row 1: Name, URL, Description, Active.
const SHEET_CSV_URL =
  'https://docs.google.com/spreadsheets/d/e/2PACX-1vQYBVBwrzFwC9WMOldD8QkVhjbZTXq1eUkqhz9anZApOoEt1_kHpal5r62ZElMKQX71nZDGRGY2C8J4/pub?gid=0&single=true&output=csv';

const YT_ID = 'gpw_za42ZGM';
const DISCORD_URL = 'https://discord.gg/yJQjVJUbEA';

// Tight amber spotlight ground — the same dark radial vignette used across the
// site. No card; text and elements float directly in the world.
const VIGNETTE =
  'radial-gradient(ellipse 90% 80% at 50% 50%, rgba(13,12,11,0.9) 0%, rgba(13,12,11,0.62) 55%, transparent 100%)';

// Minimal RFC-4180-ish CSV parser: handles quoted fields, escaped "" quotes,
// embedded commas/newlines, and CRLF line endings.
const parseCSV = (text) => {
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      row.push(field);
      field = '';
    } else if (ch === '\n') {
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else if (ch !== '\r') {
      field += ch;
    }
  }
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows.filter((r) => r.some((c) => c.trim() !== ''));
};

const toActiveLinks = (csv) => {
  const rows = parseCSV(csv);
  if (rows.length < 2) return [];
  const headers = rows[0].map((h) => h.trim().toLowerCase());
  const col = (name) => headers.indexOf(name);
  const ni = col('name');
  const ui = col('url');
  const di = col('description');
  const ai = col('active');
  return rows
    .slice(1)
    .map((r) => ({
      name: (r[ni] || '').trim(),
      url: (r[ui] || '').trim(),
      description: (r[di] || '').trim(),
      active: (r[ai] || '').trim(),
    }))
    .filter((l) => l.active.toLowerCase() === 'yes' && l.name && l.url);
};

// Quiet Space Mono section label over a hairline that fades to the right.
const Label = ({ children }) => (
  <div className="mb-4 flex items-center gap-4">
    <span className="font-mono text-[10px] uppercase tracking-[0.34em] text-cream/75">{children}</span>
    <span className="h-px flex-1 bg-gradient-to-r from-cream/12 to-transparent" />
  </div>
);

// A single editorial link entry. At rest it's quiet type; on hover the name
// warms, the row eases right, the arrow lifts to amber, and a thin amber rule
// draws across the full width — one restrained moment, nothing louder.
const LinkRow = ({ href, name, description, active, delay, testId }) => (
  <motion.a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testId}
    initial={{ opacity: 0, y: 14 }}
    animate={active ? { opacity: 1, y: 0 } : { opacity: 0, y: 14 }}
    transition={{ delay, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
    className="group relative block py-5"
  >
    <div className="flex items-center justify-between gap-6">
      <span className="min-w-0 transition-transform duration-500 ease-out group-hover:translate-x-1.5">
        <span className="block font-sans text-lg font-medium tracking-tight text-cream transition-colors duration-300 group-hover:text-cream-warm">
          {name}
        </span>
        {description && (
          <span className="mt-1.5 block font-sans text-sm leading-relaxed text-cream/55">{description}</span>
        )}
      </span>
      <ArrowUpRight className="h-5 w-5 shrink-0 text-cream/25 transition-all duration-300 ease-out group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-[#c17a2a]" />
    </div>
    <span className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-cream/[0.08]" />
    <span className="pointer-events-none absolute bottom-0 left-0 h-px w-0 bg-[#c17a2a]/70 transition-all duration-700 ease-out group-hover:w-full" />
  </motion.a>
);

export const LinksCard = ({ active }) => {
  const [links, setLinks] = useState([]);
  const [playing, setPlaying] = useState(false);
  const [videoTitle, setVideoTitle] = useState('');

  useEffect(() => {
    let cancelled = false;
    fetch(SHEET_CSV_URL)
      .then((r) => (r.ok ? r.text() : Promise.reject(r.status)))
      .then((text) => {
        // Newest first: the sheet appends new rows at the bottom, so reverse
        // and keep only the two most recent active links.
        if (!cancelled) setLinks(toActiveLinks(text).reverse().slice(0, 2));
      })
      .catch(() => {
        // Fetch failed — render nothing for the sponsored part, silently.
      });

    // Pull the video's real title from YouTube's oembed so it stays accurate.
    fetch(
      `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${YT_ID}&format=json`
    )
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => {
        if (!cancelled && d && d.title) setVideoTitle(d.title);
      })
      .catch(() => {
        // Title unavailable — the embed simply renders without a caption.
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={active ? { opacity: 1 } : { opacity: 0 }}
      transition={{ duration: 0.7, ease: 'easeOut' }}
      className="w-[min(660px,90vw)] p-7 sm:p-12"
      style={{ background: VIGNETTE }}
      data-testid="links-card"
    >
      {/* Section identity — a single warm accent, then a serif headline */}
      <p className="mb-4 font-mono text-[10px] uppercase tracking-[0.4em] text-[#c17a2a]/80">Links</p>
      <h2 className="font-display text-[2rem] leading-[1.08] text-cream sm:text-[2.6rem]">
        <WordsPullUp text="Everything in one place." active={active} />
      </h2>
      <div className="mt-6 h-px w-full bg-gradient-to-r from-cream/15 via-cream/5 to-transparent" />

      {/* PART 1 — Sponsored links from the Google Sheet (silent if empty/failed) */}
      {links.length > 0 && (
        <div className="mt-10" data-testid="links-featured">
          <Label>Featured</Label>
          <div className="-mt-5">
            {links.map((l, i) => (
              <LinkRow
                key={`${l.url}-${i}`}
                href={l.url}
                name={l.name}
                description={l.description}
                active={active}
                delay={0.15 + i * 0.08}
                testId={`links-featured-${i}`}
              />
            ))}
          </div>
        </div>
      )}

      {/* PART 2 — Latest YouTube video as a cinematic card, title overlaid */}
      <div className="mt-10" data-testid="links-youtube">
        <Label>Latest Video</Label>
        <div className="group relative aspect-video w-full overflow-hidden rounded-2xl border border-cream/10 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.85)]">
          {playing ? (
            <iframe
              src={`https://www.youtube.com/embed/${YT_ID}?autoplay=1`}
              title="Latest Video"
              loading="lazy"
              allow="accelerated-sensors; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              className="h-full w-full"
            />
          ) : (
            <button
              type="button"
              onClick={() => setPlaying(true)}
              aria-label="Play latest video"
              data-testid="links-youtube-play"
              className="absolute inset-0 h-full w-full text-left"
            >
              <img
                src={`https://img.youtube.com/vi/${YT_ID}/maxresdefault.jpg`}
                alt="Latest Video thumbnail"
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-1000 ease-out group-hover:scale-[1.04]"
              />
              {/* cinematic scrim so the overlaid text always reads */}
              <span className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
              {/* refined play button */}
              <span className="absolute inset-0 flex items-center justify-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full border border-cream/50 bg-black/20 backdrop-blur-md transition-all duration-500 ease-out group-hover:scale-105 group-hover:border-cream group-hover:bg-black/30">
                  <svg viewBox="0 0 24 24" className="ml-0.5 h-5 w-5 fill-cream" aria-hidden="true">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </span>
              </span>
              {/* overlaid kicker + title, bottom-left */}
              <span className="absolute inset-x-0 bottom-0 p-6">
                <span className="font-mono text-[10px] uppercase tracking-[0.34em] text-[#c17a2a]">Watch</span>
                {videoTitle && (
                  <span
                    className="mt-2 block max-w-[88%] font-sans text-base font-medium leading-snug text-cream sm:text-lg"
                    data-testid="links-youtube-title"
                  >
                    {videoTitle}
                  </span>
                )}
              </span>
            </button>
          )}
        </div>
      </div>

      {/* PART 3 — Discord, a quiet tile that warms on hover */}
      <div className="mt-10" data-testid="links-discord">
        <Label>Community</Label>
        <motion.a
          href={DISCORD_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="links-discord-row"
          initial={{ opacity: 0, y: 14 }}
          animate={active ? { opacity: 1, y: 0 } : { opacity: 0, y: 14 }}
          transition={{ delay: 0.2, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="group flex items-center gap-4 rounded-2xl border border-cream/10 bg-cream/[0.02] p-5 transition-all duration-300 hover:border-[#c17a2a]/40 hover:bg-[rgba(193,122,42,0.05)]"
        >
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-cream/10 bg-black/30 text-cream/80 transition-colors duration-300 group-hover:text-[#c17a2a]">
            <DiscordIcon className="h-5 w-5" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block font-sans text-base font-medium text-cream">Join the Discord</span>
            <span className="mt-0.5 block font-sans text-sm leading-snug text-cream/55">
              The group chat for everything tech, AI, and TKB.
            </span>
          </span>
          <ArrowUpRight className="h-5 w-5 shrink-0 text-cream/30 transition-all duration-300 ease-out group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-cream" />
        </motion.a>
      </div>
    </motion.div>
  );
};

export default LinksCard;
